import axios from "axios";

/** Notifications on this phone or computer: asking permission, joining (or leaving) the server's list, and keeping the two in step. */

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && window.isSecureContext !== false;
export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
export const permission = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

// WHO turned this device on (and has not turned it off since). Only that person's device is quietly put right when the browser drops its subscription, and only they get it back when they sign in again:
// someone else signing in on the same phone is never switched on without asking. (Older versions stored "1": it counts for whoever signs in first, then becomes theirs.)
const WANT = "hp-push-wanted";
const wantedBy = () => { try { return localStorage.getItem(WANT) || ""; } catch { return ""; } };
const wantedFor = (uid) => { const w = wantedBy(); return !!w && (w === "1" || w === uid); };
const setWanted = (uid) => { try { if (uid) localStorage.setItem(WANT, uid); else localStorage.removeItem(WANT); } catch { /* private window: nothing to remember */ } };

const keyBytes = (b64) => { const pad = "=".repeat((4 - (b64.length % 4)) % 4); const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };

async function registration() {
  try { const existing = await navigator.serviceWorker.getRegistration(); if (existing && existing.active) return existing; } catch { /* fall through to waiting for it */ }
  // A phone app that has been closed for a while can take a long time to wake its service worker: wait for it properly rather than deciding after a few seconds that there is none.
  return Promise.race([navigator.serviceWorker.ready, new Promise((_, rej) => setTimeout(() => rej(new Error("The app isn't ready for notifications yet. Reload the page and try again.")), 25000))]);
}

// The address of this device's subscription, remembered so the screen can still tell whether the server has this device when the browser is slow to answer (it must never read "off" just because the phone didn't reply in time).
const EP = "hp-push-endpoint";
const rememberEndpoint = (ep) => { try { if (ep) localStorage.setItem(EP, ep); else localStorage.removeItem(EP); } catch { /* private window */ } };
export const knownEndpoint = () => { try { return localStorage.getItem(EP) || ""; } catch { return ""; } };

/** This device's current subscription, or null when it has none. */
export async function currentSubscription() {
  return (await readSubscription()).sub;
}

/** The same, but says whether the browser really answered: { sub, known }. known is false when it could not be asked (a service worker slow to wake); that is not the same as "no subscription". */
export async function readSubscription() {
  if (!pushSupported()) return { sub: null, known: true };
  try { return { sub: await (await registration()).pushManager.getSubscription(), known: true }; } catch { return { sub: null, known: false }; }
}

/** The browser's own reasons, in words a person can act on. */
export function explainPushError(e) {
  if (e && e.response) return null;                                  // a server answer: the caller shows the server's words
  const name = e && e.name, msg = (e && e.message) || "";
  if (name === "NotAllowedError") return "Notifications were blocked or the question was dismissed. Allow notifications for this app in your phone's settings, then try again.";
  if (name === "AbortError" || /push service/i.test(msg)) return "This phone couldn't reach its notification service (Google or Apple). Check the phone is online and try again in a minute. Phones without Google services can't receive web notifications.";
  if (name === "InvalidStateError") return "This device has an old notification setup that is stuck. Use \"Reset notifications on this device\" below.";
  if (name === "NotSupportedError" || name === "SecurityError") return "This browser can't receive notifications here. On an iPhone, add the app to the Home Screen first.";
  return msg || "Notifications couldn't be turned on.";
}

/** Throws away whatever subscription this device had and makes a new one. A subscription the push service no longer accepts (the server removes those) otherwise stays on the phone, looks "on", and never delivers. */
async function freshSubscription(reg, publicKey) {
  let replaces = "";
  try { const old = await reg.pushManager.getSubscription(); if (old) { replaces = old.endpoint; await old.unsubscribe(); } } catch { /* it may already be gone */ }
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  return { sub, replaces };
}

async function tellServer(api, { sub, replaces }) {                 // `replaces` is the old address, so the server doesn't keep a dead one beside the new
  const j = sub.toJSON();
  await axios.post(`${api}/push/subscribe`, { endpoint: j.endpoint, keys: j.keys, userAgent: navigator.userAgent.slice(0, 200), replaces: replaces && replaces !== j.endpoint ? replaces : "" });
  rememberEndpoint(j.endpoint);
}

/** Asks the person's permission (only ever from a tap), subscribes this device afresh and tells the server. `publicKey` is the server's key from the panel, so nothing is waited for between the tap and the permission and subscribe calls (iPhones want those inside the tap). Throws when it can't: use explainPushError. */
export async function enablePush(api, publicKey, uid) {
  if (!pushSupported()) throw new Error("This browser can't receive notifications.");
  const result = await Notification.requestPermission();
  if (result !== "granted") throw new Error(result === "denied" ? "Notifications are blocked for this site. Allow them in your browser or phone settings." : "Notifications weren't turned on.");
  const key = publicKey || (await axios.get(`${api}/push/config`)).data.publicKey;
  const made = await freshSubscription(await registration(), key);
  await tellServer(api, made);
  setWanted(uid || "1");
  return made.sub;
}

/** Turns notifications off for this device only. */
export async function disablePush(api) {
  setWanted(false);
  rememberEndpoint("");
  const sub = await currentSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await axios.post(`${api}/push/unsubscribe`, { endpoint }); } catch { /* the server drops a dead address by itself */ }
  try { await sub.unsubscribe(); } catch { /* already gone */ }
}

/** On signing out: this phone stops getting that person's notifications, whoever signs in next, but it is only PAUSED: it keeps its place and the browser keeps its subscription, so it comes back by itself when
 *  the same person signs in again (before, signing out removed it and notifications "turned themselves off"). Turning it off for good is disablePush. Never blocks the sign-out. */
export async function pauseThisDevice(api) {
  try {
    if (permission() !== "granted") return;
    const ep = (await currentSubscription())?.endpoint || knownEndpoint();
    if (ep) await axios.post(`${api}/push/pause`, { endpoint: ep }, { silent: true });
  } catch { /* ignore */ }
}

/** When the app opens signed in and this device already has permission: make sure the server has a working copy of this device's subscription. If the server's key changed, or the server no longer lists this device (it removes addresses the push service has stopped accepting), subscribe afresh (no prompt: permission was already given). */
export async function syncPush(api, uid) {
  try {
    if (!pushSupported() || permission() !== "granted") return;
    const { sub, known } = await readSubscription();
    if (!known) return;                                              // the browser was slow to answer: that says nothing about the subscription, so leave everything as it is
    if (!sub) {                                                       // the browser dropped it (they do, after a long time closed): permission is still given, so subscribe again without asking
      if (!wantedFor(uid)) return;
      const { data } = await axios.get(`${api}/push/config`, { silent: true });
      await tellServer(api, await freshSubscription(await registration(), data.publicKey));
      setWanted(uid || "1");
      return;
    }
    const { data } = await axios.get(`${api}/push/config`, { params: { endpoint: sub.endpoint }, silent: true });
    const have = sub.options?.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
    const want = keyBytes(data.publicKey);
    const sameKey = have && have.length === want.length && !have.some((v, i) => v !== want[i]);
    if (sameKey && data.thisDevice) { setWanted(uid || "1"); return; }    // all in step: a device that is on remembers it, so it can put itself right later
    if (sameKey && data.paused) { await tellServer(api, { sub, replaces: "" }); setWanted(uid || "1"); return; }   // this person signed out of this phone and is back: switch it on again, nothing new to ask or make
    if (!wantedFor(uid)) return;                                      // the server has no record of this person on this phone and they never turned it on here (a shared phone): leave it off until they choose
    await tellServer(api, await freshSubscription(await registration(), data.publicKey));
    setWanted(uid || "1");
  } catch { /* try again next time */ }
}

/** "Reset notifications on this device": the same as turning off and on, in one go, for a device that says it is on but isn't getting anything. */
export async function resetPush(api, publicKey, uid) {
  if (permission() !== "granted") throw new Error("Notifications are blocked for this site. Allow them in your browser or phone settings.");
  const made = await freshSubscription(await registration(), publicKey || (await axios.get(`${api}/push/config`)).data.publicKey);
  await tellServer(api, made);
  setWanted(uid || "1");
  return made.sub;
}
